import { Fragment, useRef, useState, type ReactElement, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { DotMenuIcon, DownloadIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../buttons/button/Button.js';
import { type IntentProps } from '../../../../core/slots/Intent/Intent.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { DATATABLE_COMPONENT_NAME } from '../../constants.js';
import { dataTableMessages } from '../../messages.js';
import { DataTableDownloadData } from './Download/DataTableDownloadData.js';

interface ToolbarMenuProps {
  /**
   * Indicates whether the `DownloadData` slot is used, to determine if the download action should be rendered in the menu.
   */
  hasDownloadData: boolean;
  /**
   * Array of column IDs that can optionally be excluded from the download, passed down to the `DownloadData` slot.
   */
  downloadExcludedColumns?: string[];
  /**
   * Name of the downloaded CSV file, passed down to the `DownloadData` slot.
   */
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string);
  /**
   * Array of intent props for the `Intent` slot, to render the intent actions in the menu.
   */
  intents: Array<IntentProps>;
}

/**
 * The menu component for the `DownloadData` and `Intent` slots.
 * @internal
 */
export const DataTableToolbarMenu = (
  props: ToolbarMenuProps,
): JSX.Element | undefined => {
  const { hasDownloadData, downloadExcludedColumns, fileName, intents } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const hasIntents = intents.length > 0;

  const triggerIcon = hasIntents ? <DotMenuIcon /> : <DownloadIcon />;
  const triggerDataDtChildrenName = 'OpenToolbarMenu';

  const Group = hasIntents ? Menu.Group : Fragment;

  const intl = useIntl();
  let toolbarMenuMessage = dataTableMessages.downloadButtonLabel;

  if (isDownloading) {
    toolbarMenuMessage = dataTableMessages.downloadingButtonLabel;
  } else if (hasIntents) {
    toolbarMenuMessage = dataTableMessages.toolbarButtonLabel;
  }

  const toolbarMenuLabel = intl.formatMessage(toolbarMenuMessage);
  const simpleButtonRef = useRef<HTMLButtonElement>(null);

  // do not render anything if neither the `DownloadData` nor the `Intent` slot is used
  if (!hasDownloadData && !hasIntents) {
    return;
  }

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (menuOpen === false) {
    return (
      <Tooltip text={toolbarMenuLabel}>
        <Button
          ref={simpleButtonRef}
          variant="default"
          aria-label={toolbarMenuLabel}
          aria-haspopup="menu"
          aria-expanded={false}
          onClick={() => setMenuOpen(true)}
          loading={isDownloading}
          data-dt-children-name={triggerDataDtChildrenName}
        >
          <Button.Prefix>{triggerIcon}</Button.Prefix>
        </Button>
      </Tooltip>
    );
  }

  // according to the GUIDEline intent items with app ids are grouped
  // intent items that don't have an app id are positioned below that group
  const {
    intentsWithAppOrIntentId: intentsWithAppId,
    intentsWithoutAppOrIntentId: intentsWithoutAppId,
  } = intents.reduce<{
    intentsWithAppOrIntentId: ReactElement[];
    intentsWithoutAppOrIntentId: ReactElement[];
  }>(
    (acc, intentProps, idx) => {
      const hasAppOrIntentId =
        intentProps.options.recommendedAppId !== undefined ||
        intentProps.options.recommendedIntentId !== undefined;

      const intent = (
        <Menu.Intent key={`intent-${idx}`} {...intentProps}>
          {intentProps.icon && <Menu.Prefix>{intentProps.icon}</Menu.Prefix>}
          {intentProps.children}
        </Menu.Intent>
      );

      hasAppOrIntentId
        ? acc.intentsWithAppOrIntentId.push(intent)
        : acc.intentsWithoutAppOrIntentId.push(intent);
      return acc;
    },
    { intentsWithAppOrIntentId: [], intentsWithoutAppOrIntentId: [] },
  );

  return (
    <Menu
      open={menuOpen}
      onOpenChange={(open) => {
        // Since the elements are actually swapped when the menu is open / closed
        // the focus needs to move back to the button only render.
        if (open === false) {
          requestAnimationFrame(() => {
            simpleButtonRef.current?.focus();
          });
        }
        setMenuOpen(open);
      }}
      modal={false}
      data-dt-component={DATATABLE_COMPONENT_NAME}
    >
      <Tooltip text={toolbarMenuLabel}>
        <Menu.Trigger>
          <Button
            variant="default"
            aria-label={toolbarMenuLabel}
            data-dt-children-name={triggerDataDtChildrenName}
          >
            <Button.Prefix>{triggerIcon}</Button.Prefix>
          </Button>
        </Menu.Trigger>
      </Tooltip>

      <Menu.Content alignment="end">
        {hasDownloadData && (
          <Group>
            <DataTableDownloadData
              excludeColumns={downloadExcludedColumns}
              fileName={fileName}
              hasIntents={hasIntents}
              isDownloading={isDownloading}
              onDownloadStart={() => setIsDownloading(true)}
              onDownloadEnd={() => setIsDownloading(false)}
            />
          </Group>
        )}
        {intentsWithAppId.length > 0 && <Group>{intentsWithAppId}</Group>}
        {intentsWithoutAppId.length > 0 && <Group>{intentsWithoutAppId}</Group>}
      </Menu.Content>
    </Menu>
  );
};
